import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { EditDocumentsComponent } from '@accounts/individual-account/edit-account/pages/edit-documents/edit-documents.component';
import { KycAccordionComponent } from '@shared/ui/kyc-accordion/kyc-accordion.component';
import { InputFileComponent } from '@shared/ui/input-file/input-file.component';
import { DocKyc } from '@core/models/admin/account';
import { TabsComponent } from '@shared/ui/tabs/tabs.component';
import { Jointkyc } from '@core/models/admin/account/joint';
import { HttpEvent, HttpEventType } from '@angular/common/http';

interface DocumentDetails {
  id: string;
  inputLabel: string;
  isUploaded: boolean;
  key: string;
  fileLink: string | null;
  documentFile: File | null;
  docIdentifier: string;
  isApproved: boolean;
  maxFileSize: number;
  acceptedFileTypes: string[];
  joint_account_id: string;
}
export interface DocList {
  [key: string]: DocumentDetails[];
}

@Component({
  selector: 'app-edit-joint-docs',
  standalone: true,
  imports: [
    CommonModule,
    KycAccordionComponent,
    InputFileComponent,
    TabsComponent,
  ],
  templateUrl: './edit-joint-docs.component.html',
  styleUrl: './edit-joint-docs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditJointDocsComponent extends EditDocumentsComponent {
  @Input() jointDataDetails!: Jointkyc[];

  profileTabs: {
    label: string;
    contentTemplate: TemplateRef<any>;
    context: { index: number };
  }[] = [];
  @ViewChild('profileTemplate', { static: true })
  profileTemplate!: TemplateRef<any>;
  newDocumentList: DocList = {}
  objectKeys = Object.keys;

  groupedDocuments: { joint_account_id: string | null; documents: DocKyc[] }[] =
    [];

  constructor() {
    super();
  }

  override ngOnInit(): void {
    this.setAccountId();
    this.initializeDocumentList();
    this.checkIsDocumentApproved();

    // Initialize groupedDocuments based on jointDataDetails
    this.groupedDocuments =
      this.jointDataDetails?.map((account) => ({
        joint_account_id: account.id,
        documents: [],
      })) || [];

    if (this.jointDataDetails?.length) {
      this.setFormValues();
    }

    if (this.kycDocuments?.length) {
      this.groupDocuments();
    }

  }

  initializeDocumentList() {
    Object.keys(this.documentList).forEach((docType) => {
      this.newDocumentList[docType] = [];

      // Create one entry per joint account
      const accountCount = this.jointDataDetails?.length || 1;
      for (let i = 0; i < accountCount; i++) {
        this.newDocumentList[docType].push({
          ...this.documentList[docType], // Copy all original properties
          id: `${this.documentList[docType].id}_${i}`, // Ensure unique IDs
          joint_account_id: this.jointDataDetails[i]?.id || '',
        });
      }
    });
  }
  
  override checkIsDocumentApproved() {
    if (this.kycDocuments) {
      let unapprovedCounter = 0;

      for (const doc of this.kycDocuments) {
        const documentType = this.titleCase(doc.document_type);

        // Check all entries for this document type
        if (this.newDocumentList[documentType]) {
          this.newDocumentList[documentType].forEach((entry) => {
            if (entry.joint_account_id === doc.joint_account_id) {
              entry.isApproved = doc.status === 'approved';
              console.log('entryid', doc.status);
              if (doc.status !== 'approved') {
                unapprovedCounter++;
              }
            }
          });
        }
      }
      this.unapprovedDocsSubject.next(unapprovedCounter);
    }
  }

  groupDocuments() {
    for (const doc of this.kycDocuments) {
      const docJointId = doc.joint_account_id || null;

      if (!docJointId) {
        // Add to the first group if joint_account_id is null
        this.groupedDocuments[0].documents.push(doc);
      } else {
        // Find the corresponding group and push the doc into its array
        const targetGroup = this.groupedDocuments.find(
          (group) => group.joint_account_id === docJointId
        );
        if (targetGroup) {
          targetGroup.documents.push(doc);
        }
      }
    }
  }
  private getProfileLabel(accountData: Jointkyc, index: number): string {
    const { personal_details } = accountData;
    return personal_details
      ? `${personal_details.first_name} ${personal_details.last_name}`
      : `Profile ${index + 1}`;
  }

  private setFormValues(): void {
    // Initialize profile tabs
    this.profileTabs = this.jointDataDetails.map((account, index) => ({
      label: this.getProfileLabel(account, index),
      contentTemplate: this.profileTemplate,
      context: { index },
    }));
  }

  override getUploadedFile(
    fileList: FileList | null,
    documentType: string,
    jointAccountId?: string
  ): void {
    if (fileList) {
      this.documentList[documentType].isUploaded = true;
      this.documentList[documentType].documentFile = fileList[0];
      documentType === 'generalKyc'
        ? this.uploadGeneralFile()
        : this.uploadDocument(documentType, jointAccountId);
    } else {
      this.documentList[documentType].isUploaded = false;
      this.documentList[documentType].documentFile = null;
    }
  }

  override uploadDocument(docType: string, jointAccountId?: string): void {
    const formData = new FormData();
    const documentFile = this.documentList[docType].documentFile;
    if (documentFile) {
      formData.append('file', documentFile);
      formData.append(
        'document_type',
        this.documentList[docType].docIdentifier
      );
      formData.append('account_id', this.accountId);
      formData.append('joint_account_id', jointAccountId!);
    }

    const sub = this.documentService.uploadKycDocument(formData).subscribe({
      next: (response: HttpEvent<any>) => {
        if (response.type === HttpEventType.Response) {
          window.location.reload();
          this.toast.success(`Successfully uploaded Document`);
        }
      },
      error: () => {
        this.toast.error('Failed to upload Document');
      },
    });

    this.subs.push(sub);
  }
}
